import * as React from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { Language } from '../../enums';
import { useFormatDate, useParsers } from '../../hooks';
import { BoxTitleDetails, UseFormatDate, UseParsers } from '../../interfaces';

import {
  Header,
  Container,
  DotSeparator,
  ItemAttribute,
  PriceContainer,
  SimpleMarkdownMessage,
  TagLabel,
  NewsTitle,
  BiddersNumber,
  SimpleButton
} from './boxTitle.styled';
import { BoxTitleProps } from './boxTitle.types';

export const BoxTitle: React.FC<BoxTitleProps> = (props: BoxTitleProps): JSX.Element => {
  const { t }: TransProps<never> = useTranslation();
  const { boxTitleDetails, className }: BoxTitleProps = props;
  const { formatDate }: UseFormatDate = useFormatDate(Language.Pl);
  const {
    minimalPrice,
    startPrice,
    estimationPriceLow,
    estimationPriceHigh,
    price,
    minimalPriceNotReached,
    biddersNumber,
    tagLabel,
    author,
    itemName,
    itemAttribute,
    itemDescriptiveAttributes,
    itemsCount,
    eventName,
    eventEndTimestamp,
    newsTitle,
    bottomButtonLabel,
    bottomButtonOnClick,
    onBiddersNumberClick,
    currency = 'zł',
  }: BoxTitleDetails = boxTitleDetails;
  const { parsePrice }: UseParsers = useParsers();

  const bottomButtonOnClickAction: (e: React.MouseEvent<HTMLElement>) => void = (
    e: React.MouseEvent<HTMLElement>
  ): void => {
    if (!bottomButtonOnClick) {
      return;
    }
    
    e.preventDefault();
    bottomButtonOnClick();
  };

  return (
    <Container className={className}>
      {eventName && <Header>{eventName}</Header>}
      {eventEndTimestamp && (
        <ItemAttribute>
          {t(
            'onarte.ui.boxTitle.eventEnded',
            { value: formatDate(eventEndTimestamp, 'DD MMMM, HH:mm') }
          )}
        </ItemAttribute>
      )}
      {tagLabel && <TagLabel>{tagLabel}</TagLabel>}
      {newsTitle && <NewsTitle>{newsTitle}</NewsTitle>}
      {author && <Header>{author}</Header>}
      {itemName && <ItemAttribute>{itemName}{itemAttribute && `, ${itemAttribute}`}</ItemAttribute>}
      {!!itemDescriptiveAttributes?.length && itemDescriptiveAttributes.map((attribute: string): JSX.Element => (
        <ItemAttribute key={attribute}>{attribute}</ItemAttribute>
      ))}
      {itemsCount !== undefined && (
        <ItemAttribute>{`${t('onarte.ui.boxTitle.itemsCount')} ${itemsCount}`}</ItemAttribute>
      )}
      {startPrice !== undefined && (
        <ItemAttribute>{`${t('onarte.ui.boxTitle.startPrice')} ${parsePrice(startPrice)} ${currency}`}</ItemAttribute>
      )}
      {minimalPrice !== undefined && (
        <ItemAttribute>{`${t('onarte.ui.boxTitle.minimalPrice')} ${parsePrice(minimalPrice)} ${currency}`}</ItemAttribute>
      )}
      {(estimationPriceLow !== undefined && estimationPriceHigh !== undefined) && (
        <ItemAttribute>
          {`${t('onarte.ui.boxTitle.estimation')} ${parsePrice(estimationPriceLow)}-${parsePrice(estimationPriceHigh)} ${currency}`}
        </ItemAttribute>
      )}
      {minimalPriceNotReached && (
        <SimpleMarkdownMessage text={t('onarte.ui.boxTitle.minimalPriceNotReached')} />
      )}
      {(price !== undefined || biddersNumber !== undefined) && (
        <PriceContainer>
          {price !== undefined && `${parsePrice(price)} ${currency}`}
          {(price !== undefined && biddersNumber !== undefined) && <DotSeparator />}
          {biddersNumber !== undefined && (
            <BiddersNumber
              onClick={onBiddersNumberClick}
              $withOnClick={!!biddersNumber && !!onBiddersNumberClick}
            >
              {biddersNumber
                ? t('onarte.common.numberOfBidders', { offers: biddersNumber })
                : t('onarte.common.noOneIsBidding')
              }
            </BiddersNumber>
          )}
        </PriceContainer>
      )}
      {bottomButtonLabel && (
        <SimpleButton onClick={bottomButtonOnClickAction}>{bottomButtonLabel}</SimpleButton>
      )}
    </Container>
  );
};
